import React, { useEffect, useState } from 'react';

/**
 * 瑜伽体式图片组件
 * 专门用于显示瑜伽体式图片，包含错误处理和调试信息
 */
const YogaPoseImage = ({ 
  src, 
  alt, 
  poseName = '',
  className = '', 
  style = {}, 
  showDebugInfo = true,
  ...props 
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState(src);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    setImageSrc(src);
    setImageError(false);
    setRetryCount(0);
  }, [src]);

  // 从URL中提取文件名
  const getFilenameFromSrc = (url) => {
    if (!url) return '未知文件';
    try {
      const urlParts = url.split('/');
      const filename = urlParts[urlParts.length - 1];
      return filename || '未知文件';
    } catch (error) {
      return '未知文件';
    }
  };

  // 获取文件扩展名
  const getFileExtension = (filename) => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
  };

  const handleImageError = (e) => {
    const filename = getFilenameFromSrc(src);
    console.warn('🖼️ 瑜伽体式图片加载失败:', {
      src,
      poseName,
      filename,
      retryCount
    });
    
    setImageError(true);
  };

  const handleImageLoad = () => {
    console.log('✅ 瑜伽体式图片加载成功:', {
      src,
      poseName,
      retryCount
    });
    setImageError(false);
  };

  const handleRetry = () => {
    const newRetryCount = retryCount + 1;
    setRetryCount(newRetryCount);
    setImageError(false);
    setImageSrc(src + '?retry=' + Date.now());
    console.log('🔄 重试加载图片:', { src, retryCount: newRetryCount });
  };

  // 如果图片加载失败，显示错误信息和文件名
  if (imageError) {
    const filename = getFilenameFromSrc(src);
    const fileExtension = getFileExtension(filename);
    
    return (
      <div 
        className={`flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-6 ${className}`}
        style={style}
        {...props}
      >
        {/* 瑜伽图标 */}
        <div className="text-gray-400 mb-3">
          <svg 
            className="w-16 h-16" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" 
            />
          </svg>
        </div>
        
        {/* 体式名称 */}
        {poseName && (
          <h4 className="text-lg font-medium text-gray-700 mb-2 text-center">
            {poseName}
          </h4>
        )}
        
        {/* 错误信息 */}
        <div className="text-center mb-4">
          <p className="text-sm font-medium text-red-600 mb-2">
            🖼️ 图片加载失败
          </p>
          
          {/* 文件信息 */}
          <div className="bg-white rounded-lg p-3 border border-gray-200 mb-3">
            <p className="text-xs text-gray-500 mb-1">文件名:</p>
            <p className="text-sm font-mono text-gray-800 bg-gray-100 px-2 py-1 rounded">
              {filename}
            </p>
            
            {fileExtension && (
              <p className="text-xs text-gray-500 mt-2">
                格式: <span className="font-medium">{fileExtension}</span>
              </p>
            )}
          </div>
          
          {/* 调试信息 */}
          {showDebugInfo && (
            <details className="text-left">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                调试信息 (点击展开)
              </summary>
              <div className="mt-2 p-2 bg-gray-50 rounded text-xs font-mono">
                <p><strong>完整路径:</strong> {src}</p>
                <p><strong>体式名称:</strong> {poseName || '未指定'}</p>
                <p><strong>重试次数:</strong> {retryCount}</p>
                <p><strong>Alt文本:</strong> {alt}</p>
              </div>
            </details>
          )}
        </div>
        
        {/* 操作按钮 */}
        <div className="flex space-x-2">
          <button
            onClick={handleRetry}
            className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            🔄 重试加载
          </button>
          
          <button
            onClick={() => {
              navigator.clipboard.writeText(filename);
              alert('文件名已复制到剪贴板');
            }}
            className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
          >
            📋 复制文件名
          </button>
        </div>
        
        {/* 建议 */}
        <p className="text-xs text-gray-500 mt-3 text-center max-w-xs">
          请检查文件是否存在于 <code className="bg-gray-200 px-1 rounded">/static/yoga/</code> 目录中
        </p>
      </div>
    );
  }

  // 正常显示图片
  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      style={style}
      onError={handleImageError}
      onLoad={handleImageLoad}
      {...props}
    />
  );
};

export default YogaPoseImage;
